// frontend/src/pages/AdminDashboard.jsx
import { useState, useEffect } from "react";
import api, { getStorageUrl } from "../config/api";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Download,
  Shield,
  Building2,
  MapPin,
  Mail,
  CreditCard,
  Loader2,
  Phone,
  ChevronDown,
  ChevronUp,
  UserCheck,
  UserX,
  Clock,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Filter,
  Gavel,
  RotateCcw,
  FileText,
  Check,
  X,
  Trash2,
  Image,
  Plus,
  Edit2,
  Link,
  EyeOff,
  Eye as EyeIcon,
  Move,
  Sparkles,
  MessageCircle,
  Home,
} from "lucide-react";

// Importar los componentes
import ModerationPanel from "../components/admin/ModerationPanel";
import BannerManagement from "../components/admin/BannerManagement";
import FeaturedManagement from "../components/admin/FeaturedManagement";
import ReportsManagement from "../components/admin/ReportsManagement";
import ChatReportsManagement from "../components/admin/ChatReportsManagement";
import UserTypeBadge from '../components/UserTypeBadge';
import UnidadeCard from '../components/admin/UnidadeCard';
import WhatsAppBotPanel from '../components/admin/WhatsAppBotPanel'; // 👈 YA IMPORTADO

export default function AdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [viewMode, setViewMode] = useState('unidades');
  const [loading, setLoading] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [expandedAddresses, setExpandedAddresses] = useState({});
  const [userFilter, setUserFilter] = useState("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeMainTab, setActiveMainTab] = useState("users");
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [reports, setReports] = useState([]);
  const [chatReports, setChatReports] = useState([]);

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    pendentes: 0,
    aprovados: 0,
    rejeitados: 0,
    porCondominio: {}
  });

  useEffect(() => {
    fetchAllUsers();
    fetchUnidades();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, userFilter, searchTerm]);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users/all');
      setUsers(response.data);
      calculateStats(response.data);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
      Swal.fire("Erro!", "Não foi possível carregar os usuários.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await api.get('/admin/unidades');
      setUnidades(response.data);
    } catch (error) {
      console.error("Error al cargar unidades:", error);
    }
  };

  const calculateStats = (usersData) => {
    const pendentes = usersData.filter(u => u.status === 'pendente').length;
    const aprovados = usersData.filter(u => u.status === 'aprovado').length;
    const rejeitados = usersData.filter(u => u.status === 'rejeitado').length;
    
    const porCondominio = {};
    usersData.forEach(u => {
      const condNome = u.condominio?.nome || "Sem condomínio";
      porCondominio[condNome] = (porCondominio[condNome] || 0) + 1;
    });

    setStats({
      total: usersData.length,
      pendentes,
      aprovados,
      rejeitados,
      porCondominio
    });
  };

  const fetchReports = async () => {
    try {
      const response = await api.get('/admin/reports');
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error("Error al cargar denuncias:", error);
    }
  };

  const fetchChatReports = async () => {
    try {
      const response = await api.get('/admin/chat-reports');
      if (response.data.success) {
        setChatReports(response.data.data);
      }
    } catch (error) {
      console.error("Error al cargar denuncias de chat:", error);
    }
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await api.get('/admin/profile-requests');
      setRequests(response.data);
    } catch (error) {
      console.error("Error al cargar solicitudes:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const approveUpdateRequest = async (request) => {
    const result = await Swal.fire({
      title: 'Aprovar solicitação?',
      html: `
        <p>Usuário: <strong>${request.user?.name}</strong></p>
        <p>Campos solicitados: ${Object.keys(request.new_data || {}).join(', ')}</p>
        <p>Novos valores: ${JSON.stringify(request.new_data)}</p>
        <p class="text-sm text-gray-500 mt-2">Esta ação irá atualizar os dados do usuário.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, aprovar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await api.post(`/admin/profile-requests/${request.id}/approve`);
        Swal.fire({
          icon: "success",
          title: "Solicitação aprovada!",
          text: "Os dados do usuário foram atualizados.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchRequests();
        fetchAllUsers();
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível aprovar a solicitação.", "error");
      }
    }
  };

  const rejectRequest = async (request) => {
    const { value: reason } = await Swal.fire({
      title: 'Rejeitar solicitação?',
      html: `
        <p>Usuário: <strong>${request.user?.name}</strong></p>
        <p>Tipo: ${request.request_type === 'update' ? 'Alteração de dados' : 'Exclusão de conta'}</p>
        <textarea id="reason" class="swal2-textarea mt-3" placeholder="Motivo da rejeição..." rows="3"></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById('reason').value;
        if (!reason) {
          Swal.showValidationMessage('Informe o motivo da rejeição');
          return false;
        }
        return reason;
      }
    });

    if (reason) {
      try {
        await api.post(`/admin/profile-requests/${request.id}/reject`, { reason });
        Swal.fire({
          icon: "success",
          title: "Solicitação rejeitada!",
          text: "O usuário será notificado.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchRequests();
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível rejeitar a solicitação.", "error");
      }
    }
  };

  const processDeleteAccount = async (request) => {
    const result = await Swal.fire({
      title: 'Processar exclusão de conta?',
      html: `
        <p>Usuário: <strong>${request.user?.name}</strong></p>
        <p>Email: ${request.user?.email}</p>
        <p class="text-sm text-red-600 mt-2">⚠️ Esta ação é irreversível! Todos os dados do usuário serão removidos.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, excluir conta",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/users/${request.user_id}/force-delete`);
        Swal.fire({
          icon: "success",
          title: "Conta excluída!",
          text: "O usuário foi removido permanentemente.",
          timer: 3000,
          showConfirmButton: false
        });
        fetchRequests();
        fetchAllUsers();
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível excluir a conta.", "error");
      }
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (userFilter !== "todos") {
      filtered = filtered.filter(u => u.status === userFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.cpf?.includes(term) ||
        u.phone?.includes(term)
      );
    }
    
    setFilteredUsers(filtered);
  };

  const updateUserStatus = async (userId, newStatus, reason = null) => {
    const action = newStatus === 'aprovado' ? 'aprovar' : 'rejeitar';
    const confirmText = newStatus === 'aprovado' ? 'Aprovar usuário?' : 'Rejeitar usuário?';
    const confirmMessage = newStatus === 'aprovado' 
      ? 'Este usuário poderá publicar produtos após a aprovação.'
      : 'Informe o motivo da rejeição:';

    if (newStatus === 'rejeitado') {
      const { value: motivo } = await Swal.fire({
        title: confirmText,
        text: confirmMessage,
        input: "textarea",
        inputPlaceholder: "Digite o motivo da rejeição...",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Rejeitar",
        cancelButtonText: "Cancelar",
        inputValidator: (value) => {
          if (!value) {
            return "Por favor, informe o motivo da rejeição.";
          }
        }
      });

      if (!motivo) return;
      reason = motivo;
    } else {
      const result = await Swal.fire({
        title: confirmText,
        text: confirmMessage,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Sim, aprovar",
        cancelButtonText: "Cancelar"
      });

      if (!result.isConfirmed) return;
    }

    try {
      const endpoint = newStatus === 'aprovado' 
        ? `/admin/users/${userId}/approve`
        : `/admin/users/${userId}/reject`;
      
      const payload = reason ? { reason } : {};
      
      await api.post(endpoint, payload);
      
      Swal.fire({
        icon: "success",
        title: newStatus === 'aprovado' ? "Aprovado!" : "Rejeitado!",
        text: newStatus === 'aprovado' ? "Usuário aprovado com sucesso." : "Usuário rejeitado.",
        timer: 2000,
        showConfirmButton: false
      });
      
      fetchAllUsers();
      fetchUnidades();
    } catch (error) {
      Swal.fire("Erro!", `Não foi possível ${action} o usuário.`, "error");
    }
  };

  const revertRejection = async (userId, userName) => {
    const result = await Swal.fire({
      title: 'Reverter rejeição?',
      html: `
        <p>Deseja reverter a rejeição do usuário <strong>${userName}</strong>?</p>
        <p class="text-sm text-gray-500 mt-2">O usuário voltará para o estado <strong>pendente</strong> e poderá ser aprovado novamente.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, reverter",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await api.post(`/admin/users/${userId}/revert-rejection`);
        Swal.fire({
          icon: "success",
          title: "Rejeição revertida!",
          text: "Usuário voltou para estado pendente.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchAllUsers();
        fetchUnidades();
      } catch (error) {
        console.error("Error al revertir:", error);
        Swal.fire("Erro!", "Não foi possível reverter a rejeição.", "error");
      }
    }
  };

  const revertToPending = async (userId, userName) => {
    const result = await Swal.fire({
      title: 'Reverter para pendente?',
      html: `
        <p>Deseja reverter o usuário <strong>${userName}</strong> para <strong>pendente</strong>?</p>
        <p class="text-sm text-gray-500 mt-2">O usuário precisará ser aprovado novamente para publicar.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: "#eab308",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, reverter",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await api.post(`/admin/users/${userId}/revert-to-pending`);
        Swal.fire({
          icon: "success",
          title: "Revertido!",
          text: "Usuário voltou para estado pendente.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchAllUsers();
        fetchUnidades();
      } catch (error) {
        console.error("Error al revertir:", error);
        Swal.fire("Erro!", "Não foi possível reverter o usuário.", "error");
      }
    }
  };

  const rejectApprovedUser = async (userId, userName) => {
    const { value: motivo } = await Swal.fire({
      title: 'Rejeitar usuário aprovado?',
      html: `
        <p>Usuário: <strong>${userName}</strong></p>
        <p class="text-sm text-gray-500 mt-2">Este usuário está <strong>aprovado</strong>. Ao rejeitá-lo, ele perderá o acesso de publicação.</p>
        <textarea id="reason" class="swal2-textarea mt-3" placeholder="Motivo da rejeição..." rows="3"></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById('reason').value;
        if (!reason) {
          Swal.showValidationMessage('Informe o motivo da rejeição');
          return false;
        }
        return reason;
      }
    });

    if (motivo) {
      try {
        await api.post(`/admin/users/${userId}/reject`, { reason: motivo });
        Swal.fire({
          icon: "success",
          title: "Rejeitado!",
          text: "Usuário foi rejeitado.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchAllUsers();
        fetchUnidades();
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível rejeitar o usuário.", "error");
      }
    }
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const toggleAddressExpand = (userId) => {
    setExpandedAddresses(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const formatFullAddress = (userItem) => {
    const endereco = userItem.address;
    if (!endereco) return null;
    
    const parts = [];
    if (endereco.logradouro) parts.push(endereco.logradouro);
    if (endereco.numero) parts.push(endereco.numero);
    if (endereco.complemento) parts.push(endereco.complemento);
    if (endereco.bairro) parts.push(endereco.bairro);
    if (endereco.cidade) parts.push(endereco.cidade);
    if (endereco.uf) parts.push(endereco.uf);
    if (endereco.cep) parts.push(`CEP: ${endereco.cep}`);
    
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'aprovado':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap"><CheckCircle className="w-3 h-3" /> Aprovado</span>;
      case 'rejeitado':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap"><XCircle className="w-3 h-3" /> Rejeitado</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 whitespace-nowrap"><Clock className="w-3 h-3" /> Pendente</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#449879] animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-2 px-4 pb-24 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#449879]" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Painel Administrativo</h1>
          </div>
          <p className="text-gray-500">Gerencie todos os usuários e modere a plataforma</p>
        </div>

        {/* Tabs principales */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveMainTab("users")}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "users"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Gestão de Usuários</span>
            <span className="sm:hidden">Usuários</span>
            <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {stats.total}
            </span>
          </button>
          <button
            onClick={() => setActiveMainTab("moderation")}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "moderation"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Gavel className="w-4 h-4" />
            <span className="hidden sm:inline">Moderação</span>
            <span className="sm:hidden">Mod.</span>
          </button>
          <button
            onClick={() => {
              setActiveMainTab("requests");
              fetchRequests();
            }}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "requests"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Solicitações</span>
            <span className="sm:hidden">Solic.</span>
            {requests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {requests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveMainTab("banners")}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "banners"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Image className="w-4 h-4" />
            <span className="hidden sm:inline">Banners</span>
            <span className="sm:hidden">Banners</span>
          </button>
          <button
            onClick={() => setActiveMainTab("featured")}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "featured"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Destaques</span>
            <span className="sm:hidden">Destaques</span>
          </button>
          <button
            onClick={() => {
              setActiveMainTab("reports");
              fetchReports();
            }}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "reports"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Denúncias</span>
            <span className="sm:hidden">Denún.</span>
            {reports.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {reports.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveMainTab("chat-reports");
              fetchChatReports();
            }}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "chat-reports"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Denúncias Chat</span>
            <span className="sm:hidden">Chat</span>
            {chatReports.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {chatReports.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          {/* 🔥 NUEVA PESTAÑA: WHATSAPP BOT */}
          <button
            onClick={() => setActiveMainTab("whatsapp")}
            className={`px-4 md:px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 whitespace-nowrap ${
              activeMainTab === "whatsapp"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">WhatsApp Bot</span>
            <span className="sm:hidden">Bot</span>
          </button>
        </div>

        {/* Panel de Gestión de Usuarios */}
        {activeMainTab === "users" && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-6 h-6 text-blue-500" />
                  <span className="text-2xl font-bold text-gray-800">{stats.total}</span>
                </div>
                <p className="text-sm text-gray-600">Total de Usuários</p>
              </div>
              
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-6 h-6 text-yellow-500" />
                  <span className="text-2xl font-bold text-gray-800">{stats.pendentes}</span>
                </div>
                <p className="text-sm text-gray-600">Pendentes</p>
              </div>

              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <UserCheck className="w-6 h-6 text-green-500" />
                  <span className="text-2xl font-bold text-gray-800">{stats.aprovados}</span>
                </div>
                <p className="text-sm text-gray-600">Aprovados</p>
              </div>

              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <UserX className="w-6 h-6 text-red-500" />
                  <span className="text-2xl font-bold text-gray-800">{stats.rejeitados}</span>
                </div>
                <p className="text-sm text-gray-600">Rejeitados</p>
              </div>
            </div>

            {/* Gráfico de condomínios */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-8 overflow-x-auto">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Usuários por Condomínio</h2>
              </div>
              <div className="min-w-[200px] space-y-2">
                {Object.entries(stats.porCondominio).map(([nome, count]) => (
                  <div key={nome} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-40 md:w-48 truncate">{nome}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#449879] rounded-full"
                        style={{ width: `${(count / stats.total) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filtros y búsqueda */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Buscar por nome, email, CPF ou telefone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                    />
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setUserFilter("todos")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                      userFilter === "todos" 
                        ? "bg-[#449879] text-white" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => setUserFilter("pendente")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                      userFilter === "pendente" 
                        ? "bg-yellow-500 text-white" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Pendentes
                  </button>
                  <button
                    onClick={() => setUserFilter("aprovado")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                      userFilter === "aprovado" 
                        ? "bg-green-500 text-white" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Aprovados
                  </button>
                  <button
                    onClick={() => setUserFilter("rejeitado")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                      userFilter === "rejeitado" 
                        ? "bg-red-500 text-white" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Rejeitados
                  </button>
                </div>
              </div>
            </div>

            {/* Toggle de vista */}
            <div className="flex items-center gap-2 mb-4 justify-end">
              <button
                onClick={() => setViewMode('unidades')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === 'unidades' 
                    ? 'bg-[#449879] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Home className="w-4 h-4" />
                Por Unidade
              </button>
              <button
                onClick={() => setViewMode('usuarios')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === 'usuarios' 
                    ? 'bg-[#449879] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Users className="w-4 h-4" />
                Por Usuário
              </button>
            </div>

            {/* Lista de Usuários / Unidades */}
            {viewMode === 'unidades' ? (
              <div className="space-y-4">
                {unidades.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Home className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">Nenhuma unidade encontrada</p>
                    <p className="text-sm text-gray-400 mt-1">Ainda não há unidades cadastradas.</p>
                  </div>
                ) : (
                  unidades.map((unidade) => (
                    <UnidadeCard 
                      key={unidade.id} 
                      unidade={unidade}
                      onUserAction={(userId, newStatus) => {
                        updateUserStatus(userId, newStatus);
                      }}
                    />
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredUsers.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">Nenhum usuário encontrado</p>
                    <p className="text-sm text-gray-400 mt-1">Tente ajustar os filtros de busca</p>
                  </div>
                ) : (
                  filteredUsers.map((userItem) => (
                    <div key={userItem.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleUserExpand(userItem.id)}
                      >
                        <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#449879] to-[#2d5f4d] rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0">
                              {userItem.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-semibold text-gray-800 truncate max-w-[120px] sm:max-w-[200px] md:max-w-none">
                                  {userItem.name}
                                </h3>
                                <UserTypeBadge tipoResidente={userItem.tipo_residente} size="sm" />
                                <div className="flex flex-wrap gap-1">
                                  {getStatusBadge(userItem.status)}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                {userItem.warning_count > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600 whitespace-nowrap">
                                    ⚠️ {userItem.warning_count}
                                  </span>
                                )}
                                {userItem.is_permanently_banned === 1 && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 whitespace-nowrap">
                                    🚫 Banido
                                  </span>
                                )}
                                {userItem.suspended_until && new Date(userItem.suspended_until) > new Date() && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600 whitespace-nowrap">
                                    ⏰ Suspenso
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1 sm:gap-3 text-xs text-gray-500 mt-1">
                                <span className="truncate">📧 {userItem.email}</span>
                                <span>📱 {userItem.phone || "Sem telefone"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-row items-center gap-2 shrink-0">
                            {userItem.status === 'pendente' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateUserStatus(userItem.id, 'aprovado');
                                  }}
                                  className="bg-green-50 text-green-600 p-2 rounded-xl hover:bg-green-100 transition-colors"
                                  title="Aprovar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateUserStatus(userItem.id, 'rejeitado');
                                  }}
                                  className="bg-red-50 text-red-600 p-2 rounded-xl hover:bg-red-100 transition-colors"
                                  title="Rejeitar"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {userItem.status === 'aprovado' && !userItem.is_permanently_banned && (
                              <div className="flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    revertToPending(userItem.id, userItem.name);
                                  }}
                                  className="bg-yellow-50 text-yellow-600 p-2 rounded-xl hover:bg-yellow-100 transition-colors"
                                  title="Reverter"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    rejectApprovedUser(userItem.id, userItem.name);
                                  }}
                                  className="bg-red-50 text-red-600 p-2 rounded-xl hover:bg-red-100 transition-colors"
                                  title="Rejeitar"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {userItem.status === 'rejeitado' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  revertRejection(userItem.id, userItem.name);
                                }}
                                className="bg-blue-50 text-blue-600 p-2 rounded-xl hover:bg-blue-100 transition-colors"
                                title="Reverter"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            
                            <div className="shrink-0">
                              {expandedUsers[userItem.id] ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {expandedUsers[userItem.id] && (
                        <div className="p-4 border-t border-gray-100 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <div className="flex items-start gap-2">
                                <CreditCard className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                                <div className="overflow-hidden">
                                  <p className="text-xs text-gray-400">CPF</p>
                                  <p className="text-sm text-gray-700 break-all">{userItem.cpf}</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <Building2 className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                                <div>
                                  <p className="text-xs text-gray-400">Condomínio</p>
                                  <p className="text-sm text-gray-700">{userItem.condominio?.nome || "Não informado"}</p>
                                </div>
                              </div>
                              {userItem.suspension_reason && (
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">Motivo da suspensão/banimento</p>
                                    <p className="text-sm text-red-600">{userItem.suspension_reason}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-3">
                              {formatFullAddress(userItem) && (
                                <div>
                                  <button
                                    onClick={() => toggleAddressExpand(userItem.id)}
                                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#449879] transition-colors"
                                  >
                                    <MapPin className="w-4 h-4" />
                                    <span>Ver endereço completo</span>
                                    {expandedAddresses[userItem.id] ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                  </button>
                                  {expandedAddresses[userItem.id] && (
                                    <div className="mt-2 p-3 bg-white rounded-xl">
                                      <p className="text-sm text-gray-600 break-words">{formatFullAddress(userItem)}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {userItem.comprovante_residencia && (
                                <a
                                  href={getStorageUrl(userItem.comprovante_residencia)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-[#449879] hover:underline"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>Ver comprovante</span>
                                  <Download className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Panel de Moderación */}
        {activeMainTab === "moderation" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 p-5 bg-gray-50">
              <div className="flex items-center gap-2">
                <Gavel className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Moderação de Usuários</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie advertências, suspensões e banimentos por violação das normas
              </p>
            </div>
            <div className="p-5 overflow-x-auto">
              <ModerationPanel />
            </div>
          </div>
        )}

        {/* Panel de Solicitações */}
        {activeMainTab === "requests" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Solicitações dos Usuários</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie solicitações de alteração de dados e exclusão de conta
              </p>
            </div>

            {loadingRequests ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#449879] animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Carregando solicitações...</p>
              </div>
            ) : requests.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma solicitação pendente</p>
                <p className="text-sm text-gray-400 mt-1">Os usuários não enviaram solicitações ainda.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {requests.map((req) => (
                  <div key={req.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-800 break-words">{req.user?.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                            req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {req.status === 'pending' ? 'Pendente' : req.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-2 break-all">📧 {req.user?.email}</p>
                        
                        {req.request_type === 'update' ? (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg overflow-x-auto">
                            <p className="text-sm font-medium text-gray-700 mb-2">📝 Solicitação de alteração:</p>
                            <p className="text-sm text-gray-600 break-words">
                              <strong>Campos:</strong> {Object.keys(req.new_data || {}).join(', ')}
                            </p>
                            <p className="text-sm text-gray-600 mt-1 break-words">
                              <strong>Novos valores:</strong> {Object.entries(req.new_data || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                            </p>
                            <p className="text-sm text-gray-600 mt-1 break-words">
                              <strong>Motivo:</strong> {req.reason}
                            </p>
                          </div>
                        ) : (
                          <div className="mt-2 p-3 bg-red-50 rounded-lg">
                            <p className="text-sm font-medium text-red-700 mb-2">🗑️ Solicitação de exclusão de conta</p>
                            <p className="text-sm text-red-600 break-words">
                              <strong>Motivo:</strong> {req.reason}
                            </p>
                          </div>
                        )}

                        <p className="text-xs text-gray-400 mt-2">
                          Solicitado em: {new Date(req.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>

                      {req.status === 'pending' && (
                        <div className="flex flex-wrap gap-2 shrink-0">
                          {req.request_type === 'update' ? (
                            <button
                              onClick={() => approveUpdateRequest(req)}
                              className="px-4 py-2 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                            >
                              <Check className="w-4 h-4" />
                              Aprovar
                            </button>
                          ) : (
                            <button
                              onClick={() => processDeleteAccount(req)}
                              className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir conta
                            </button>
                          )}
                          <button
                            onClick={() => rejectRequest(req)}
                            className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                          >
                            <X className="w-4 h-4" />
                            Rejeitar
                          </button>
                        </div>
                      )}

                      {req.status !== 'pending' && req.admin_response && (
                        <div className="text-sm text-gray-500 max-w-md">
                          <p><strong>Resposta:</strong> {req.admin_response}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Panel de Banners */}
        {activeMainTab === "banners" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 p-5 bg-gray-50">
              <div className="flex items-center gap-2">
                <Image className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Gestão de Banners</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie os banners que aparecem no carousel principal da Home
              </p>
            </div>
            <div className="p-5">
              <BannerManagement />
            </div>
          </div>
        )}

        {/* Panel de Destaques */}
        {activeMainTab === "featured" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 p-5 bg-gray-50">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Gestão de Destaques</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie os itens que aparecem na seção de Destaques da Home
              </p>
            </div>
            <div className="p-5">
              <FeaturedManagement />
            </div>
          </div>
        )}

        {/* Panel de Denúncias de Anuncios */}
        {activeMainTab === "reports" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 p-5 bg-gray-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Gestão de Denúncias</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie as denúncias de anúncios feitas pelos usuários
              </p>
            </div>
            <div className="p-5">
              <ReportsManagement />
            </div>
          </div>
        )}

        {/* Panel de Denúncias de Chat */}
        {activeMainTab === "chat-reports" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 p-5 bg-gray-50">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#449879]" />
                <h2 className="font-semibold text-gray-800">Denúncias de Chat</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Gerencie as denúncias de mensagens e conversas feitas pelos usuários
              </p>
            </div>
            <div className="p-5">
              <ChatReportsManagement />
            </div>
          </div>
        )}

        {/* 🔥 PANEL DE WHATSAPP BOT */}
        {activeMainTab === "whatsapp" && (
          <WhatsAppBotPanel />
        )}
      </div>
    </div>
  );
}